<template>
  <b-modal
    v-model="showModal"
    :title="$t('whatsapp-users')"
    size="lg"
    @hidden="closeModal"
  >
    <div class="whatsapp-users-container">
      <div class="info-section">
        <h5 class="info-title">{{ $t('configuracao-envio-relatorios') }}</h5>
        <p class="info-text">
          {{ $t('selecione-usuarios-relatorio-turno') }}
        </p>
        <div class="selected-count" :class="{ 'text-danger': selectedUsers.length === 5 }">
          {{ selectedUsers.length }}/5 {{ $t('usuarios-selecionados') }}
        </div>
      </div>

      <div class="users-list">
        <div v-for="user in availableUsers" :key="user.id" class="user-item">
          <div class="user-checkbox">
            <b-form-checkbox
              v-model="selectedUsers"
              :value="user.id"
              :disabled="selectedUsers.length >= 5 && !selectedUsers.includes(user.id)"
            >
              {{ user.name }}
            </b-form-checkbox>
          </div>
          <div class="input-container" v-if="selectedUsers.includes(user.id)">
            <label :for="'whatsapp-'+user.id" class="input-label">{{ $t('numero-whatsapp') }}:</label>
            <b-form-input
              class="custom-input-color"
              :id="'whatsapp-'+user.id"
              v-model="whatsappNumbers[user.id]"
              type="text"
              pattern="[0-9]*"
              inputmode="numeric"
              :placeholder="$t('placeholder-telefone')"
              maxlength="15"
              @input="formatPhoneNumber(user.id)"
              @keypress="onlyNumbers"
            />
            <small class="form-text text-muted">
              {{ $t('digite-apenas-numeros-ddd') }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <div class="d-flex justify-content-end w-100">
        <b-button variant="secondary" class="mr-2" @click="closeModal">
          {{ $t('cancelar') }}
        </b-button>
        <b-button
          class="btn btn-primary"
          :disabled="!canSend"
          @click="handleSend"
        >
          {{ $t('salvar') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'WhatsAppUserModal',
  
  props: {
    modelname: {
      type: String,
      required: true
    },
    initialUsers: {
      type: Array,
      default: () => []
    },
    preSelectedUsers: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      showModal: true,
      selectedUsers: [],
      whatsappNumbers: {},
      availableUsers: []
    }
  },

  created() {
    this.initializeData()
  },

  computed: {
    canSend() {
      return this.selectedUsers.length > 0 && 
             this.selectedUsers.every(userId => 
               this.whatsappNumbers[userId] && 
               this.whatsappNumbers[userId].replace(/\D/g, '').length === 11
             )
    }
  },

  methods: {
    initializeData() {
      this.availableUsers = this.initialUsers;
      
      // Inicializa os usuários pré-selecionados
      this.selectedUsers = this.preSelectedUsers.map(user => user.userId);
      
      // Inicializa os números de WhatsApp dos usuários pré-selecionados
      this.preSelectedUsers.forEach(user => {
        if (user.whatsAppNumber) {
          this.$set(this.whatsappNumbers, user.userId, user.whatsAppNumber);
          // Formata o número para exibição
          this.formatPhoneNumber(user.userId);
        }
      });
    },

    closeModal() {
      this.showModal = false;
      this.$emit('close');
    },

    handleSend() {
      if (!this.canSend) return;
      
      // Prepara os dados para enviar para a API
      const allUsers = this.availableUsers.map(user => {
        // Remove a formatação antes de enviar
        let phoneNumber = null;
        if (this.selectedUsers.includes(user.id) && this.whatsappNumbers[user.id]) {
          phoneNumber = this.whatsappNumbers[user.id].replace(/\D/g, '');
        }
        
        return {
          userId: user.id,
          whatsAppNumber: this.selectedUsers.includes(user.id) ? phoneNumber : null,
          isActive: this.selectedUsers.includes(user.id)
        };
      });
      
      this.$emit('send', allUsers);
    },

    onlyNumbers(e) {
      const char = String.fromCharCode(e.keyCode);
      if (/[^0-9]/.test(char)) {
        e.preventDefault();
      }
    },

    formatPhoneNumber(userId) {
      // Remove qualquer caractere não numérico
      let number = this.whatsappNumbers[userId]?.replace(/\D/g, '') || '';
      
      // Limita a 11 dígitos numéricos
      if (number.length > 11) {
        number = number.substring(0, 11);
      }
      
      // Formata para exibição com parênteses e hífen
      let formattedNumber = number;
      if (number.length > 0) {
        // Adiciona parênteses no DDD
        formattedNumber = number.length <= 2 ? `(${number}` : `(${number.substring(0, 2)})`;
        
        // Adiciona o restante dos números após o DDD
        if (number.length > 2) {
          formattedNumber += ` ${number.substring(2)}`;
        }
        
        // Adiciona o hífen antes dos últimos 4 dígitos
        if (number.length > 7) {
          formattedNumber = formattedNumber.substring(0, formattedNumber.length - 4) + '-' + 
                          formattedNumber.substring(formattedNumber.length - 4);
        }
      }
      
      // Atualiza o valor formatado para exibição
      this.$set(this.whatsappNumbers, userId, formattedNumber);
    }
  }
}
</script>

<style scoped>
.whatsapp-users-container {
  max-height: 400px;
  overflow-y: auto;
  padding: 1rem;
}

.info-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid #009a9a;
}

.info-title {
  color: #009a9a;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.info-text {
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.selected-count {
  font-weight: 500;
  color: #198754;
}

.selected-count.text-danger {
  color: #ff751b !important;
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.user-item {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: white;
}

.user-checkbox {
  flex: 1;
  min-width: 200px;
}

.input-container {
  flex: 1;
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
}

.input-label {
  font-size: 0.875rem;
  color: #495057;
  margin-bottom: 0.25rem;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

/* Estilização da barra de rolagem */
.whatsapp-users-container::-webkit-scrollbar {
  width: 8px;
}

.whatsapp-users-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.whatsapp-users-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.whatsapp-users-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.user-item:hover {
  border-color: #009a9a;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.form-text {
  font-size: 0.75rem;
}

.btn-primary {
    background-color: #009a9a;
    border: 1px solid #009a9a;
    color: white;
}

.btn-primary:hover {
    background-color: #007a7a;
}

.custom-input-color {
  border: 1px solid #009a9a !important;
  border-radius: 4px;
  padding: 5px;
  font-size: 16px;
}

.custom-input-color:focus {
  border-color: #009a9a !important;
  box-shadow: 0 0 0 0.2rem rgba(0,154,154,0.25) !important;
}

</style>
